import { forwardRef, useImperativeHandle, useRef } from 'react';
import { DateTime } from 'luxon';
import DatePicker from '../DatePicker';
import TimePicker from '../TimePicker';
import { DateHelper } from '../../../helpers/date.helper';
import ValidationInfo from '../../../validation/validation-info';
import type { IServerValidationError } from '../validatable.interface';
import type IValidatable from '../validatable.interface';
import { TranslationService } from '../../../services/translation.service';

export interface IDateTimePickerState {
  value: DateTime;
  validationInfo: ValidationInfo;
}

export interface IDateTimePickerProps {
  selectedDateTime?: DateTime;
  minDateTime?: DateTime;
  maxDateTime?: DateTime;
  minuteStep?: number;
  legend?: string;
  showLegend?: boolean;
  layoutSmRow?: boolean;
  layoutLgColumn?: boolean;
  showAsteriskOnRequiredFields?: boolean;
  ariaDescribedBy?: string | undefined;
  serverValidationPrefix?: string | undefined;
  serverValidationName?: string | undefined;
  state: IDateTimePickerState;
  onChange?: ((value: DateTime) => void) | undefined;
}

export default forwardRef<IValidatable, IDateTimePickerProps>(function DateTimePicker(
  {
    minDateTime = DateTime.now().startOf('day').minus({ days: 30 }),
    maxDateTime = DateTime.now().endOf('day').plus({ days: 30 }),
    minuteStep = 5,
    legend = TranslationService.get('common.selecttime'),
    showLegend = false,
    layoutSmRow = false,
    layoutLgColumn = false,
    showAsteriskOnRequiredFields = false,
    ariaDescribedBy,
    serverValidationPrefix = '',
    serverValidationName = 'datetime',
    state,
    onChange,
  }: IDateTimePickerProps,
  ref
) {
  // === REFERENCES ===
  const datePickerRef = useRef<IValidatable>(null!);
  const timePickerRef = useRef<IValidatable>(null!);

  // === VARIABLES ===

  // === INTERFACE IMPLEMENTATION ===
  useImperativeHandle(ref, () => ({
    checkValidity(): ValidationInfo {
      return new ValidationInfo('', [], { date: datePickerRef.current.checkValidity(), time: timePickerRef.current.checkValidity() });
    },
    handleServerValidationErrors(errors: IServerValidationError[]): ValidationInfo {
      return new ValidationInfo(
        '',
        errors
          .filter((e) => e.name.toLocaleLowerCase() === `${serverValidationPrefix}${serverValidationName}`.toLocaleLowerCase())
          .map((e) => e.errorMessages)
          .reduce((a, b) => a.concat(b), []),
        {
          date: datePickerRef.current.handleServerValidationErrors(errors),
          time: timePickerRef.current.handleServerValidationErrors(errors),
        }
      );
    },
  }));

  // === EVENT HANDLERS ===
  function handleDateChange(value: string): void {
    onChange?.(DateTime.fromFormat(`${value} ${state.value.toFormat(DateHelper.TIME_FORMAT)}`, `${DateHelper.DATE_FORMAT} ${DateHelper.TIME_FORMAT}`));
  }

  function handleTimeChange(value: string): void {
    onChange?.(DateTime.fromFormat(`${state.value.toFormat(DateHelper.DATE_FORMAT)} ${value}`, `${DateHelper.DATE_FORMAT} ${DateHelper.TIME_FORMAT}`));
  }

  return (
    <fieldset>
      {legend && <legend className={showLegend ? '' : 'visually-hidden'}>{legend}</legend>}
      <div className={`datetimepicker w-100 d-flex flex-column ${layoutSmRow ? 'flex-sm-row' : ''} ${layoutLgColumn ? 'flex-lg-column' : ''} ${state.validationInfo.isInvalid ? 'is-invalid' : ''}`}>
        <div className={`w-100 mb-2 ${layoutSmRow ? 'me-sm-3 mb-sm-0' : ''} ${layoutLgColumn ? 'me-lg-0 mb-lg-2' : ''}`}>
          <DatePicker
            ref={datePickerRef}
            minDate={minDateTime.toFormat(DateHelper.DATE_FORMAT)}
            maxDate={maxDateTime.toFormat(DateHelper.DATE_FORMAT)}
            showAsteriskOnRequiredFields={showAsteriskOnRequiredFields}
            ariaDescribedBy={ariaDescribedBy}
            serverValidationName={`${serverValidationPrefix}date`}
            state={{
              value: state.value.toFormat(DateHelper.DATE_FORMAT),
              validationInfo: state.validationInfo.children.date ?? new ValidationInfo(),
            }}
            onChange={handleDateChange}
          />
        </div>
        <div className={`w-100 ${layoutSmRow ? 'ms-sm-3' : ''} ${layoutLgColumn ? 'ms-lg-0' : ''}`}>
          <TimePicker
            ref={timePickerRef}
            showAsteriskOnRequiredFields={showAsteriskOnRequiredFields}
            minuteStep={minuteStep}
            ariaDescribedBy={ariaDescribedBy}
            serverValidationName={`${serverValidationPrefix}time`}
            state={{
              value: state.value.toFormat(DateHelper.TIME_FORMAT),
              validationInfo: state.validationInfo.children.time ?? new ValidationInfo(),
            }}
            onChange={handleTimeChange}
          />
        </div>
      </div>
      {state.validationInfo.getErrors().map((error) => (
        <span id="date-time-error" key={error} className="invalid-feedback ">
          {error}
        </span>
      ))}
    </fieldset>
  );
});
